import { motion } from 'framer-motion';
import { Award, Shield, Heart, Users } from 'lucide-react';
import { IMAGES } from '@/assets/images';
import { springPresets, staggerContainer, staggerItem } from '@/lib/motion';

export default function About() {
  const values = [
    {
      icon: Shield,
      title: 'Expertise',
      description: 'Specialized training in handling and grooming large, powerful breeds with confidence and care.',
    },
    {
      icon: Award,
      title: 'Professional Standards',
      description: 'Certified groomers with years of experience working exclusively with guardian and working breeds.',
    },
    {
      icon: Heart,
      title: 'Respect',
      description: 'We treat every dog with the dignity and respect they deserve, understanding their unique temperament.',
    },
    {
      icon: Users,
      title: 'Owner Partnership',
      description: 'Collaborative approach to grooming, educating owners on proper care and maintenance for their breeds.',
    },
  ];

  return (
    <div className="min-h-screen">
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.LARGE_BREEDS_6}
            alt="Professional large breed dog"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight">
              Built for Serious Dogs
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              We specialize in grooming large, powerful breeds with the expertise and confidence they deserve. No cute boutiques, no small-dog mentality—just professional service for serious dogs.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="max-w-3xl mx-auto mb-16 text-center"
          >
            <h2 className="text-4xl font-bold mb-6">Our Story</h2>
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>
                Our story started with our own 130-pound XL Bully. Like so many large dog owners, we struggled to find groomers who would even take him. When we finally found places that would, they charged premium prices that were simply unaffordable for regular grooming, and the results were disappointing—they'd rush through the appointment and miss all the important nooks and crevices that big dogs need attention to.
              </p>
              <p>
                We knew there had to be a better way. We love big dogs specifically—their strength, their loyalty, their presence. They deserve grooming services that understand their unique needs without breaking the bank. That's why we founded this salon: to make professional grooming easy and affordable for large breed owners who've been underserved for too long.
              </p>
              <p>
                Our approach is built on genuine passion for large breeds. We take the time to do the job right, getting into every fold, wrinkle, and crevice. We charge fair prices because we believe every big dog deserves quality care, not just the ones with wealthy owners. This isn't just a business for us—it's our mission to serve the large dog community we're proud to be part of.
              </p>
            </div>
          </motion.div>

          <div className="relative">
            <img
              src={IMAGES.INDUSTRIAL_INTERIOR_9}
              alt="Professional grooming facility"
              className="w-full h-96 object-cover rounded-lg"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent rounded-lg" />
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="max-w-3xl mx-auto mb-16 text-center"
          >
            <h2 className="text-4xl font-bold mb-6">Our Values</h2>
            <p className="text-xl text-muted-foreground">
              The principles that guide everything we do
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto"
          >
            {values.map((value) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  variants={staggerItem}
                  className="bg-card border border-border rounded-lg p-8 hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-4">{value.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-4xl font-bold mb-6">Why Choose Us</h2>
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>
                <strong className="text-foreground">Specialized Expertise:</strong> We only work with large breeds. This focus allows us to develop deep expertise in handling, grooming techniques, and breed-specific care that general groomers simply don't have.
              </p>
              <p>
                <strong className="text-foreground">Professional Facility:</strong> Our salon is designed for large dogs—reinforced grooming tables, industrial-grade equipment, and spacious work areas that accommodate dogs of any size comfortably.
              </p>
              <p>
                <strong className="text-foreground">Confident Handling:</strong> Our team understands powerful dogs. We read body language, respect boundaries, and work with confidence that puts both dogs and owners at ease.
              </p>
              <p>
                <strong className="text-foreground">No Compromises:</strong> We don't cut corners or rush through appointments. Large breeds require time, patience, and proper technique—that's exactly what we provide.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
